import { useRef, useState, type CSSProperties, type DragEvent, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { brandColorPattern, formatFileSize } from '@brimo/shared';
import { clsx } from '../../../ds/clsx';
import { api, errorMessage } from '../../../lib/api';
import { organizationKeys, uploadWithProgress } from './api';
import type { Brand, LogoSlot } from './types';
import styles from './BrandParts.module.css';

const slotLabels: Record<LogoSlot, string> = {
  light: 'Hell · auf Papier',
  dark: 'Dunkel · auf Navy',
  favicon: 'Favicon · 64 px',
};

const slotNames: Record<LogoSlot, string> = {
  light: 'Logo hell',
  dark: 'Logo dunkel',
  favicon: 'Favicon',
};

export interface LogoSlotsProps {
  brand: Brand;
  /** The settings page (E20) draws 104 px slots with gap 16, a navy dark slot and „Noch nicht hinterlegt". */
  variant: 'wizard' | 'settings';
  /** Called after an upload or removal answered with the new brand. */
  onChanged?: ((brand: Brand) => void) | undefined;
  /** Slots shown (Admin M2 only uploads the light logo). */
  slots?: readonly LogoSlot[] | undefined;
}

/** The logo slots of Admin 03 / Einstellungen 20: drop or pick an SVG/PNG, progress, the scanned file as preview. */
export function LogoSlots({
  brand,
  variant,
  onChanged,
  slots = ['light', 'dark', 'favicon'],
}: LogoSlotsProps) {
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const [progress, setProgress] = useState<Partial<Record<LogoSlot, number>>>({});

  async function upload(slot: LogoSlot, file: File) {
    setError(null);
    setProgress((current) => ({ ...current, [slot]: 0 }));
    const form = new FormData();
    form.append('file', file);
    try {
      const next = await uploadWithProgress<Brand>(
        `/settings/organization/brand/logo/${slot}`,
        form,
        (fraction) => setProgress((current) => ({ ...current, [slot]: fraction })),
      );
      queryClient.setQueryData(organizationKeys.brand, next);
      onChanged?.(next);
    } catch (failure) {
      setError(failure instanceof Error ? failure.message : errorMessage(failure));
    } finally {
      setProgress((current) => {
        const copy = { ...current };
        delete copy[slot];
        return copy;
      });
    }
  }

  async function remove(slot: LogoSlot) {
    setError(null);
    try {
      const next = await api<Brand>(`/settings/organization/brand/logo/${slot}`, { method: 'DELETE' });
      queryClient.setQueryData(organizationKeys.brand, next);
      onChanged?.(next);
    } catch (failure) {
      setError(errorMessage(failure));
    }
  }

  return (
    <>
      <div className={clsx(styles.slots, variant === 'settings' && styles.slotsWide)}>
        {slots.map((slot) => (
          <LogoSlotBox
            key={slot}
            slot={slot}
            brand={brand}
            variant={variant}
            progress={progress[slot]}
            onFile={(file) => void upload(slot, file)}
            onRemove={() => void remove(slot)}
          />
        ))}
      </div>
      {error ? (
        <span className={styles.error} role="alert">
          {error}
        </span>
      ) : null}
    </>
  );
}

function LogoSlotBox({
  slot,
  brand,
  variant,
  progress,
  onFile,
  onRemove,
}: {
  slot: LogoSlot;
  brand: Brand;
  variant: 'wizard' | 'settings';
  progress: number | undefined;
  onFile: (file: File) => void;
  onRemove: () => void;
}) {
  const input = useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = useState(false);
  const logo = brand.logos[slot];
  const settings = variant === 'settings';

  function onDrop(event: DragEvent) {
    event.preventDefault();
    setDragging(false);
    const file = event.dataTransfer.files[0];
    if (file) onFile(file);
  }

  let content: ReactNode;
  let tone: string | undefined;
  if (progress !== undefined) {
    content = (
      <>
        <span className={styles.slotMeta}>Wird hochgeladen …</span>
        <span
          className={styles.progress}
          role="progressbar"
          aria-label={`${slotNames[slot]} wird hochgeladen`}
        >
          <span className={styles.progressFill} style={{ width: `${Math.round(progress * 100)}%` }} />
        </span>
      </>
    );
    tone = slot === 'dark' ? styles.slotDark : undefined;
  } else if (dragging) {
    content = (
      <>
        <span className={styles.dropArrow} aria-hidden="true">
          ↓
        </span>
        <span className={styles.dropText}>Hier ablegen</span>
      </>
    );
    tone = styles.slotDrop;
  } else if (logo) {
    content =
      logo.scanStatus === 'clean' ? (
        <img className={styles.slotImage} src={logo.url} alt={slotNames[slot]} />
      ) : (
        <span className={styles.slotMeta}>
          {logo.name} · {formatFileSize(logo.size)}
          <br />
          {logo.scanStatus === 'pending' ? 'wird geprüft' : 'Die Datei wurde abgelehnt.'}
        </span>
      );
    tone = slot === 'dark' ? styles.slotDark : undefined;
  } else if (slot === 'favicon' || settings) {
    content = <span className={styles.emptyText}>{settings ? 'Noch nicht hinterlegt' : 'optional'}</span>;
    tone = styles.slotEmpty;
  } else {
    content = (
      <>
        <span className={styles.dropArrow} aria-hidden="true">
          ↓
        </span>
        <span className={styles.dropText}>Hier ablegen</span>
      </>
    );
    tone = styles.slotDrop;
  }

  return (
    <div className={styles.slot}>
      <span className={styles.slotLabel} id={`logo-${slot}-label`}>
        {slotLabels[slot]}
      </span>
      <div style={{ position: 'relative' }}>
        <button
          type="button"
          className={clsx(styles.slotBox, settings && styles.slotBoxShort, tone)}
          aria-labelledby={`logo-${slot}-label`}
          aria-describedby={`logo-${slot}-action`}
          onClick={() => input.current?.click()}
          onDragOver={(event) => {
            event.preventDefault();
            setDragging(true);
          }}
          onDragLeave={() => setDragging(false)}
          onDrop={onDrop}
        >
          {content}
          <span id={`logo-${slot}-action`} className={styles.fileInput}>
            {logo ? `${slotNames[slot]} ersetzen` : `${slotNames[slot]} hochladen`}
          </span>
        </button>
        {logo && progress === undefined ? (
          <button
            type="button"
            className={styles.remove}
            aria-label={`${slotNames[slot]} entfernen`}
            onClick={onRemove}
          >
            ×
          </button>
        ) : null}
        <input
          ref={input}
          type="file"
          accept="image/svg+xml,image/png,.svg,.png"
          className={styles.fileInput}
          tabIndex={-1}
          aria-hidden="true"
          onChange={(event) => {
            const file = event.target.files?.[0];
            event.target.value = '';
            if (file) onFile(file);
          }}
        />
      </div>
    </div>
  );
}

export interface ColorSwatchesProps {
  presets: readonly string[];
  value: string | null;
  onPick: (color: string) => void;
  /** Hex field next to the swatches (Admin 03; E20 has none). */
  withHex?: boolean | undefined;
  compact?: boolean | undefined;
}

/** The four preset swatches (selected one with the double ring) and the hex field. */
export function ColorSwatches({
  presets,
  value,
  onPick,
  withHex = false,
  compact = false,
}: ColorSwatchesProps) {
  const current = value?.toUpperCase() ?? null;
  const [hex, setHex] = useState(current ?? '');
  const [lastValue, setLastValue] = useState(current);
  if (current !== lastValue) {
    setLastValue(current);
    setHex(current ?? '');
  }
  const hexValid = brandColorPattern.test(hex);

  function commitHex() {
    const next = hex.trim().toUpperCase();
    if (brandColorPattern.test(next) && next !== current) onPick(next);
  }

  return (
    <div
      className={clsx(styles.swatches, compact && styles.swatchesCompact)}
      role="radiogroup"
      aria-label="Akzentfarbe"
    >
      {presets.map((preset) => {
        const active = current === preset.toUpperCase();
        return (
          <button
            key={preset}
            type="button"
            role="radio"
            aria-checked={active}
            aria-label={preset}
            className={clsx(styles.swatch, active && styles.swatchActive)}
            style={{ background: preset, '--swatch-color': preset } as CSSProperties}
            onClick={() => onPick(preset.toUpperCase())}
          />
        );
      })}
      {withHex ? (
        <label className={clsx(styles.hex, hex !== '' && !hexValid && styles.hexInvalid)}>
          <span className={styles.fileInput}>Farbe als Hex-Wert</span>
          <input
            className={styles.hexInput}
            value={hex}
            maxLength={7}
            spellCheck={false}
            onChange={(event) =>
              setHex(event.target.value.startsWith('#') ? event.target.value : `#${event.target.value}`)
            }
            onBlur={commitHex}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault();
                commitHex();
              }
            }}
          />
        </label>
      ) : null}
    </div>
  );
}

export interface PortalPreviewProps {
  brand: Brand;
  color: string | null;
  /** E20 draws the logo bar 2 px narrower, the text lines at 80/60 %. */
  variant: 'wizard' | 'settings';
}

/** „Vorschau Mandantenportal": navy browser bar with the host, the frame's sample greeting, the brand button. */
export function PortalPreview({ brand, color, variant }: PortalPreviewProps) {
  const dark = brand.logos.dark;
  return (
    <div className={styles.previewFrame}>
      <div className={styles.browserBar}>
        {dark && dark.scanStatus === 'clean' ? (
          <img className={styles.logoMini} src={dark.url} alt="" />
        ) : (
          <span className={clsx(styles.logoBar, variant === 'settings' && styles.logoBarNarrow)} />
        )}
        <span className={styles.host}>{brand.previewHost}</span>
      </div>
      <div className={styles.portalBody}>
        <span className={styles.greeting}>Guten Tag, Herr Brunnthaler</span>
        <span className={styles.line} style={{ width: variant === 'settings' ? '80%' : '82%' }} />
        <span className={styles.line} style={{ width: variant === 'settings' ? '60%' : '62%' }} />
        <span className={styles.previewButton} style={color ? { background: color } : undefined}>
          Unterlagen ansehen
        </span>
      </div>
    </div>
  );
}

/** „Vorschau Mailkopf": the light logo (or its placeholder), two lines, the footer „Firma · Ort". */
export function MailPreview({ brand }: { brand: Brand }) {
  const light = brand.logos.light;
  return (
    <div className={styles.previewFrame}>
      <div className={styles.mailBody}>
        {light && light.scanStatus === 'clean' ? (
          <img className={styles.logoMini} src={light.url} alt="" />
        ) : (
          <span className={styles.mailLogo} />
        )}
        <span className={styles.mailLine} style={{ width: '90%' }} />
        <span className={styles.mailLine} style={{ width: '76%' }} />
      </div>
      <div className={styles.mailFooter}>{brand.mailHeader}</div>
    </div>
  );
}

export const brandStyles = styles;
